if (!customElements.get('bundle-builder')) {
  customElements.define(
    'bundle-builder',
    class BundleBuilder extends HTMLElement {
      constructor() {
        super();

        this.items = new Map();
        this.selectedTierSize = null;
        this.tiers = JSON.parse(this.dataset.tiers || '[]').sort((a, b) => a.size - b.size);
        this.minSize = parseInt(this.dataset.minSize, 10) || 1;
        this.maxSize = this.tiers.length ? this.tiers[this.tiers.length - 1].size : this.minSize;
        this.progressFill = this.querySelector('[data-progress-fill]');
        this.progressText = this.querySelector('[data-progress-text]');
        this.tierMessage = this.querySelector('[data-tier-message]');
        this.tierPriceEl = this.querySelector('[data-tier-price]');
        this.addBtn = this.querySelector('[data-add-to-cart]');
        this.btnText = this.querySelector('[data-btn-text]');
        this.errorEl = this.querySelector('[data-error-message]');
        this.tierBadges = this.querySelectorAll('[data-tier-badge]');

        this.kitList = this.querySelector('[data-kit-list]');
        this.mobileKitList = this.querySelector('[data-mobile-kit-list]');

        this.mobileCount = this.querySelector('[data-mobile-count]');
        this.mobileAddBtn = this.querySelector('[data-mobile-add-to-cart]');
        this.mobileBtnText = this.querySelector('[data-mobile-btn-text]');

        this.mobileDrawer = this.querySelector('[data-mobile-drawer]');
        this.mobileDrawerAddBtn = this.querySelector('[data-mobile-drawer-add-to-cart]');
        this.mobileDrawerBtnText = this.querySelector('[data-mobile-drawer-btn-text]');
        this.mobileTierMessage = this.querySelector('[data-mobile-tier-message]');

        this.MAX_RETRIES = 8;
        this.RETRY_BASE_DELAY_MS = 400;
        this.OPEN_CART_DELAY_MS = 500;

        this.bindEvents();
        this.updateUI();
      }

      bindEvents() {
        this.addEventListener('click', (e) => {
          const card = e.target.closest('[data-bundle-card]');
          if (card && card.dataset.available !== 'false') {
            this.handleAddKit(card);
            return;
          }

          const tierBadge = e.target.closest('[data-tier-badge]');
          if (tierBadge) {
            this.previewTierPricing(tierBadge);
            return;
          }

          const kitPlus = e.target.closest('[data-kit-plus]');
          if (kitPlus) {
            this.handleKitListQtyChange(kitPlus.dataset.kitPlus, 1);
            return;
          }

          const kitMinus = e.target.closest('[data-kit-minus]');
          if (kitMinus) {
            this.handleKitListQtyChange(kitMinus.dataset.kitMinus, -1);
            return;
          }

          const kitRemove = e.target.closest('[data-kit-remove]');
          if (kitRemove) {
            this.handleKitListQtyChange(kitRemove.dataset.kitRemove, -Infinity);
            return;
          }

          const drawerToggle = e.target.closest('[data-mobile-drawer-toggle]');
          if (drawerToggle) {
            this.openMobileDrawer();
            return;
          }

          const drawerClose = e.target.closest('[data-mobile-drawer-close]');
          if (drawerClose) {
            this.closeMobileDrawer();
            return;
          }

          const drawerOverlay = e.target.closest('[data-mobile-drawer-overlay]');
          if (drawerOverlay) {
            this.closeMobileDrawer();
            return;
          }
        });

        if (this.addBtn) this.addBtn.addEventListener('click', () => this.addToCart());
        if (this.mobileAddBtn) this.mobileAddBtn.addEventListener('click', () => this.addToCart());
        if (this.mobileDrawerAddBtn) this.mobileDrawerAddBtn.addEventListener('click', () => {
          this.closeMobileDrawer();
          this.addToCart();
        });
      }

      handleAddKit(card) {
        if (!card) return;
        if (this.getTotalQuantity() >= this.maxSize) return;

        const variantId = card.dataset.variantId;
        const title = card.dataset.productTitle;
        const current = this.items.get(variantId);
        const qty = (current ? current.quantity : 0) + 1;

        this.items.set(variantId, { quantity: qty, title });
        this.syncCardVisuals(card, qty);
        this.updateUI();
      }

      handleKitListQtyChange(variantId, delta) {
        if (!variantId) return;
        if (delta > 0 && this.getTotalQuantity() >= this.maxSize) return;

        const current = this.items.get(variantId);
        if (!current) return;

        const qty = delta === -Infinity ? 0 : current.quantity + delta;

        if (qty <= 0) {
          this.items.delete(variantId);
        } else {
          this.items.set(variantId, { ...current, quantity: qty });
        }

        const card = this.querySelector(`[data-bundle-card][data-variant-id="${variantId}"]`);
        if (card) this.syncCardVisuals(card, Math.max(qty, 0));
        this.updateUI();
      }

      previewTierPricing(badge) {
        this.selectedTierSize = parseInt(badge.dataset.tierBadge, 10);
        this.updateUI();
      }

      syncCardVisuals(card, qty) {
        const badge = card.querySelector('[data-qty-badge]');
        const label = card.querySelector('[data-add-kit-label]');

        if (badge) {
          badge.textContent = qty;
          badge.classList.toggle('hidden', qty <= 0);
          badge.classList.remove('bundle-card__qty-badge--pulse');
          void badge.offsetWidth;
          badge.classList.add('bundle-card__qty-badge--pulse');
        }
        if (label) {
          if (qty > 0) {
            label.textContent = 'Added \u2713';
            label.classList.add('bundle-card__add-kit-btn--active');
            clearTimeout(card._addedTimer);
            card._addedTimer = setTimeout(() => {
              label.textContent = '+ Add Kit';
              label.classList.remove('bundle-card__add-kit-btn--active');
            }, 1200);
          } else {
            clearTimeout(card._addedTimer);
            label.textContent = '+ Add Kit';
            label.classList.remove('bundle-card__add-kit-btn--active');
          }
        }
        card.classList.toggle('bundle-card--selected', qty > 0);
      }

      getTotalQuantity() {
        let total = 0;
        this.items.forEach((item) => (total += item.quantity));
        return total;
      }

      getCurrentTier() {
        const qty = this.getTotalQuantity();
        let current = null;
        for (const tier of this.tiers) {
          if (qty >= tier.size) current = tier;
        }
        return current;
      }

      getNextTier() {
        const qty = this.getTotalQuantity();
        for (const tier of this.tiers) {
          if (qty < tier.size) return tier;
        }
        return null;
      }

      renderKitList() {
        if (this.items.size === 0) {
          const empty = '<p class="bundle-builder__kit-list-empty">No kits added yet.</p>';
          if (this.kitList) this.kitList.innerHTML = empty;
          if (this.mobileKitList) this.mobileKitList.innerHTML = empty;
          return;
        }

        let html = '';
        this.items.forEach((item, variantId) => {
          html += `
            <div class="bundle-builder__kit-item">
              <span class="bundle-builder__kit-name">${item.title}</span>
              <div class="bundle-builder__kit-controls">
                <button type="button" class="bundle-builder__kit-qty-btn" data-kit-minus="${variantId}" aria-label="Decrease">−</button>
                <span class="bundle-builder__kit-qty">${item.quantity}</span>
                <button type="button" class="bundle-builder__kit-qty-btn" data-kit-plus="${variantId}" aria-label="Increase">+</button>
                <button type="button" class="bundle-builder__kit-remove-btn" data-kit-remove="${variantId}" aria-label="Remove">✕</button>
              </div>
            </div>`;
        });

        if (this.kitList) this.kitList.innerHTML = html;
        if (this.mobileKitList) this.mobileKitList.innerHTML = html;
      }

      updateUI() {
        const qty = this.getTotalQuantity();
        const currentTier = this.getCurrentTier();
        const nextTier = this.getNextTier();

        let target;
        if (this.selectedTierSize && qty < this.selectedTierSize) {
          target = this.selectedTierSize;
        } else {
          this.selectedTierSize = null;
          target = nextTier ? nextTier.size : this.maxSize;
        }
        const progressPercent = Math.min((qty / target) * 100, 100);
        const canAdd = currentTier !== null && qty === currentTier.size;
        const atMax = qty >= this.maxSize;

        if (this.progressFill) this.progressFill.style.width = (qty > 0 ? Math.max(progressPercent, 3) : 0) + '%';
        if (this.progressText) this.progressText.textContent = qty + ' / ' + target + ' kits selected';

        const tierMsg = this.buildTierMessage(qty, currentTier, nextTier);
        if (this.tierMessage) this.tierMessage.textContent = tierMsg;
        if (this.mobileTierMessage) this.mobileTierMessage.textContent = tierMsg;

        if (this.tierPriceEl) {
          const displayTier = currentTier || (this.selectedTierSize
            ? this.tiers.find(t => t.size === this.selectedTierSize)
            : this.tiers[0]) || null;
          if (displayTier) {
            this.tierPriceEl.innerHTML = '<span class="bundle-builder__tier-price-current">$' + displayTier.price + '</span> <s class="bundle-builder__tier-price-compare">$' + displayTier.comparePrice + '</s>';
          } else {
            this.tierPriceEl.innerHTML = '';
          }
        }

        if (this.addBtn) this.addBtn.disabled = !canAdd;
        if (this.mobileAddBtn) this.mobileAddBtn.disabled = !canAdd;
        if (this.mobileDrawerAddBtn) this.mobileDrawerAddBtn.disabled = !canAdd;

        const btnLabel = currentTier
          ? 'Add Bundle to Cart — $' + currentTier.price
          : 'Add Bundle to Cart';
        const mobileBtnLabel = currentTier
          ? 'Add to Cart — $' + currentTier.price
          : 'Add to Cart';

        if (this.btnText) this.btnText.textContent = btnLabel;
        if (this.mobileBtnText) this.mobileBtnText.textContent = mobileBtnLabel;
        if (this.mobileDrawerBtnText) this.mobileDrawerBtnText.textContent = btnLabel;

        if (this.mobileCount) this.mobileCount.textContent = qty + '/' + target;

        this.querySelectorAll('[data-bundle-card]').forEach((card) => {
          card.classList.toggle('bundle-card--disabled', atMax && !this.items.has(card.dataset.variantId));
        });

        this.tierBadges.forEach((badge) => {
          const badgeSize = parseInt(badge.dataset.tierBadge, 10);
          badge.classList.toggle('bundle-builder__tier-card--active', currentTier && badgeSize <= currentTier.size);
        });

        this.renderKitList();
      }

      buildTierMessage(qty, currentTier, nextTier) {
        if (currentTier && !nextTier) {
          return currentTier.label + ' — $' + currentTier.price + '!';
        } else if (nextTier) {
          const remaining = nextTier.size - qty;
          return 'Add ' + remaining + ' more for $' + nextTier.price + ' bundle!';
        }
        return 'Add ' + this.minSize + ' kits to get started!';
      }

      openMobileDrawer() {
        if (!this.mobileDrawer) return;
        this.mobileDrawer.classList.add('bundle-builder__mobile-drawer--open');
        document.body.style.overflow = 'hidden';
      }

      closeMobileDrawer() {
        if (!this.mobileDrawer) return;
        this.mobileDrawer.classList.remove('bundle-builder__mobile-drawer--open');
        document.body.style.overflow = '';
      }

      async addToCart() {
        if (this.items.size === 0) return;

        const currentTier = this.getCurrentTier();
        if (!currentTier || !currentTier.bundleVariantId) {
          this.setError('Bundle configuration error. Please try again.');
          return;
        }

        // Guard: quantity must exactly match a tier size
        const qty = this.getTotalQuantity();
        if (qty !== currentTier.size) {
          this.setError('Please select exactly ' + currentTier.size + ' kits for this tier.');
          return;
        }

        const kitDetails = [];
        this.items.forEach((item, variantId) => {
          kitDetails.push({ variantId, qty: item.quantity, title: item.title });
        });

        const includesList = kitDetails.map((k) => k.qty > 1 ? k.title + ' (x' + k.qty + ')' : k.title).join(', ');
        const properties = {
          'Includes': includesList,
        };

        this.setLoading(true);
        this.setError('');

        const variantId = currentTier.bundleVariantId;

        try {
          // Retry loop: add → verify → retry if UpCart cleared the cart
          let lastErr;
          for (let attempt = 0; attempt < this.MAX_RETRIES; attempt++) {
            try {
              await this.addBundleVariant(variantId, properties);
              const verified = await this.verifyCartHasVariant(variantId);
              if (!verified) {
                throw new Error('Cart did not update yet; retrying...');
              }
              // Success — break out of retry loop
              lastErr = null;
              break;
            } catch (e) {
              lastErr = e;
              console.warn('[BundleBuilder] attempt ' + (attempt + 1) + ' failed:', e.message);
              await this.sleep(this.RETRY_BASE_DELAY_MS * (attempt + 1));
            }
          }

          if (lastErr) throw lastErr;

          // Refresh Dawn cart icon badge
          await this.refreshCartBadge();

          // Dispatch cart events for theme/app listeners
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          window.dispatchEvent(new Event('cart:refresh'));

          // Fire PostHog tracking
          if (typeof posthog !== 'undefined') {
            posthog.capture('bundle added to cart', {
              total_quantity: qty,
              tier: currentTier.label,
              price: currentTier.price,
            });
          }

          // Reset UI
          this.setLoading(false);
          this.resetAll();

          // Open UpCart after a short delay to let it hydrate
          setTimeout(() => {
            try {
              if (typeof window.upcartOpenCart === 'function') window.upcartOpenCart();
            } catch (_) {}
            try {
              const el = document.querySelector('#cart-icon-bubble');
              if (el) el.click();
            } catch (_) {}
            if (typeof window.upcartOpenCart !== 'function') {
              window.location.href = window.routes?.cart_url || '/cart';
            }
          }, this.OPEN_CART_DELAY_MS);
        } catch (err) {
          console.error('[BundleBuilder] addToCart failed:', err);
          this.setLoading(false);
          this.setError('Failed to add bundle. Redirecting to cart...');
          setTimeout(() => { window.location.href = '/cart'; }, 1500);
        }
      }

      sleep(ms) {
        return new Promise((r) => setTimeout(r, ms));
      }

      async addBundleVariant(variantId, properties) {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          credentials: 'same-origin',
          cache: 'no-store',
          body: JSON.stringify({
            items: [{ id: Number(variantId), quantity: 1, properties }],
          }),
        });

        if (!res.ok) {
          let msg = 'Failed to add bundle';
          try {
            const err = await res.json();
            msg = err.description || err.message || msg;
          } catch (_) {}
          throw new Error(msg);
        }

        return res.json();
      }

      async verifyCartHasVariant(variantId) {
        const res = await fetch('/cart.js', {
          credentials: 'same-origin',
          cache: 'no-store',
        });
        if (!res.ok) throw new Error('Failed to fetch /cart.js');
        const cart = await res.json();
        return (cart.items || []).some((item) => String(item.id) === String(variantId));
      }

      async refreshCartBadge() {
        try {
          const mount = document.querySelector('#cart-icon-bubble');
          if (!mount) return;

          const res = await fetch('/?sections=cart-icon-bubble&ts=' + Date.now(), {
            credentials: 'same-origin',
            cache: 'no-store',
          });
          if (!res.ok) return;

          const data = await res.json();
          const html = data['cart-icon-bubble'];
          if (!html) return;

          mount.innerHTML = html;
        } catch (e) {
          console.warn('[BundleBuilder] cart icon badge refresh failed', e);
        }
      }

      setLoading(loading) {
        [this.addBtn, this.mobileAddBtn].forEach((btn) => {
          if (!btn) return;
          btn.classList.toggle('loading', loading);
          btn.setAttribute('aria-disabled', loading ? 'true' : 'false');
          const spinner = btn.querySelector('.loading-overlay__spinner');
          if (spinner) spinner.classList.toggle('hidden', !loading);
        });
      }

      setError(message) {
        if (this.errorEl) this.errorEl.textContent = message;
      }

      resetAll() {
        this.items.clear();
        this.querySelectorAll('[data-bundle-card]').forEach((card) => {
          this.syncCardVisuals(card, 0);
        });
        this.updateUI();
      }
    }
  );
}
